<script lang="ts">
export default {
  name: 'CompaniesManagement',
}
</script>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { useStore } from 'vuex'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { KkLoading, KkIcon } from '../components/base'
import type { CurrentUser } from '../types'
import type { OriginalTagGroupKindAlphabet} from '../domains/management/types'
import type { Setting, BasicSetting, BasicSettingKey, CustomSetting, CustomSettingKey, OriginalTagGroupSetting, FunctionSetting, FunctionSettingKey } from '../domains/management/types'
import { originalTagGroupKinds } from '../domains/management/consts'
import { useSearchKeywordImpressionLabels } from 'src/biz_spa/domains/management/composables'

const store = useStore()
const route = useRoute()
const router = useRouter()
const { t, tm } = useI18n()
const { labels: searchKeywordImpressionLabels, readLabels } = useSearchKeywordImpressionLabels()

// ref
const currentUser = ref<CurrentUser>(gon.currentUser)

// Vuex State
const tagGroups = computed(() => store.state.companiesTagGroups.data)
const loading = computed<boolean>(() => store.state.companiesTagGroups.loading)
const company = computed(() => store.state.companiesCompany.data)
const companyLoading = computed<boolean>(() => store.state.companiesCompany.loading)
const companyApprovalFlow = computed(() => store.state.companiesPostsManagementsApprovalFlows.data)
const companyApprovalFlowLoading = computed<boolean>(() => store.state.companiesPostsManagementsApprovalFlows.loading)
const companyMapionBizBannersTemplate = computed(() => store.state.companiesMapionBizBannersTemplates.data)
const companyMapionBizBannersTemplateLoading = computed<boolean>(() => store.state.companiesMapionBizBannersTemplates.loading)
const companiesMapionBizSearchGroups = computed(() => store.state.companiesMapionBizSearchGroups.data)
const companiesMapionBizSearchGroupsLoading = computed<boolean>(() => store.state.companiesMapionBizSearchGroups.loading)

// Vuex Getters
const providedFeaturesCompetitor = computed<boolean>(() => store.getters['companiesCompany/providedFeaturesCompetitor'])
const providedFeaturesAnalysisKeyword = computed<boolean>(() => store.getters['companiesCompany/providedFeaturesAnalysisKeyword'])
const providedFeaturesProduct = computed<boolean>(() => store.getters['companiesCompany/providedFeaturesProduct'])
const providedFeaturesReplyTemplate = computed<boolean>(() => store.getters['companiesCompany/providedFeaturesReplyTemplate'])
const providedFeaturesReviewReplyApprovalFlow = computed<boolean>(() => store.getters['companiesCompany/providedFeaturesReviewReplyApprovalFlow'])
const hasAvailableProduct = computed<boolean>(() => store.getters['companiesCompany/hasAvailableProduct'])
const hasAvailableSearchGroup = computed<boolean>(() => store.getters['companiesCompany/hasAvailableSearchGroup'])
const showOnlyOwnerAuthority = computed<boolean>(() => store.getters['companiesAccessibleApi/showOnlyOwnerAuthority'])
const hasMapionBizStore = computed<boolean>(() => store.getters['companiesCompany/hasMapionBizStore'])
const providedFeaturesSnsApprovalFlow = computed<boolean>(() => store.getters['companiesCompany/providedFeaturesSnsApprovalFlow'])
const providedFeaturesPostApprovalFlow = computed<boolean>(() => store.getters['companiesCompany/providedFeaturesPostApprovalFlow'])
const providedFeaturesOriginalTagGroup = computed<boolean>(() => store.getters['companiesCompany/providedFeaturesOriginalTagGroup'])
const isDocomoPlan = computed<boolean>(() => store.getters['companiesCompany/isDocomoPlan'])
const providedFeaturesFranchisee = computed<boolean>(() => store.getters['companiesCompany/providedFeaturesFranchisee'])
const providedFeaturesArea = computed<boolean>(() => store.getters['companiesCompany/providedFeaturesArea'])
const providedFeaturesSearchPattern = computed<boolean>(() => store.getters['companiesCompany/providedFeaturesSearchPattern'])
const providedFeaturesAnalysisSearchKeywordImpression = computed<boolean>(() => store.getters['companiesCompany/providedFeaturesAnalysisSearchKeywordImpression'])

// computed
const currentCompanyUuid = computed(() => {
  const uuid = route.params.company_uuid
  return Array.isArray(uuid) ? uuid[0] : uuid
})

const canAddTagGroup = computed(() => {
  if (!providedFeaturesOriginalTagGroup.value) return false

  let count = 0
  originalTagGroupKinds.forEach(v => {
    if (tagGroups.value[v]) count++
  })
  return count < originalTagGroupKinds.length
})

const functionSettingsLoading = computed<boolean>(() => {
  return loading.value || companyApprovalFlowLoading.value || companiesMapionBizSearchGroupsLoading.value || companyMapionBizBannersTemplateLoading.value || companyLoading.value
})

const basicSettingsKeys: Array<{ key: BasicSettingKey, featureFlag: boolean }> = [
  { key: 'brands', featureFlag: true },
  { key: 'competitorBrands', featureFlag: providedFeaturesCompetitor.value },
  { key: 'keywords', featureFlag: providedFeaturesAnalysisKeyword.value },
]

const basicSettings = computed<BasicSetting[]>(() => {
  return basicSettingsKeys
    .filter(({ featureFlag }) => featureFlag)
    .map(({ key }) => {
      const setting = tm(`pages.companies.management.basicSettings.${key}`) as Setting
      if (typeof setting === 'object') {
        return {
          ...setting,
          registeredCount: tagGroups.value[key].registeredCount,
          title: tagGroups.value[key].label,
          routeObj: { name: `CompaniesManagement${key.charAt(0).toUpperCase() + key.slice(1)}`, params: { company_uuid: currentCompanyUuid.value } },
          name: key,
        } as BasicSetting
      }
      return undefined
    })
    .filter((setting): setting is BasicSetting => setting !== undefined)
})

const customSettingsKeys: Array<{ key: CustomSettingKey, featureFlag: boolean }> = [
  { key: 'searchPatterns', featureFlag: providedFeaturesSearchPattern.value },
  { key: 'replyTemplates', featureFlag: providedFeaturesReplyTemplate.value },
  { key: 'parameters', featureFlag: true },
  { key: 'productCategories', featureFlag: hasAvailableProduct.value && providedFeaturesProduct.value },
  { key: 'snsCategories', featureFlag: true },
  { key: 'franchisees', featureFlag: providedFeaturesFranchisee.value },
  { key: 'areas', featureFlag: providedFeaturesArea.value },
]

const customSettings = computed<(CustomSetting | OriginalTagGroupSetting)[]>(() => {
  const settings = customSettingsKeys
    .filter(({ featureFlag }) => featureFlag)
    .map(({ key }) => {
      const setting = tm(`pages.companies.management.customSettings.${key}`) as Setting
      if (typeof setting === 'object') {
        let description = setting.description
        if (key === 'parameters') {
          description = isDocomoPlan.value
            ? t('pages.companies.management.customSettings.parameters.descriptionExceptGoogle')
            : t('pages.companies.management.customSettings.parameters.description')
        }

        return {
          ...setting,
          description,
          registeredCount: tagGroups.value[key]?.registeredCount || 0,
          title: tagGroups.value[key]?.label || '',
          routeObj: { name: `CompaniesManagement${key.charAt(0).toUpperCase() + key.slice(1)}`, params: { company_uuid: currentCompanyUuid.value } },
          originalTagGroupKind: key.startsWith('originalTagGroup') ? key : undefined,
        } as CustomSetting | OriginalTagGroupSetting
      }
      return undefined
    })
    .filter((setting): setting is (CustomSetting | OriginalTagGroupSetting) => setting !== undefined)

  // カスタム設定
  if (providedFeaturesOriginalTagGroup.value) {
    originalTagGroupKinds.forEach(kind => {
      if (tagGroups.value[kind]) {
        const setting = makeOriginalTagGroupSetting(kind)
        if (setting) settings.push(setting)
      }
    })
  }

  return settings
})

const showSearchGroups = computed<boolean>(() => {
  return Boolean(hasMapionBizStore.value && (currentUser.value.admin || currentUser.value.distributer_uuid) && hasAvailableSearchGroup.value)
})

const showTemplates = computed<boolean>(() => {
  return Boolean(hasMapionBizStore.value && (currentUser.value.admin || currentUser.value.distributer_uuid))
})

const functionSettingsKeys: Array<{ key: FunctionSettingKey, featureFlag: boolean, routeName: string }> = [
  { key: 'snsApprovalFlow', featureFlag: providedFeaturesSnsApprovalFlow.value, routeName: 'CompaniesSnsApprovalFlowForm' },
  { key: 'postApprovalFlow', featureFlag: providedFeaturesPostApprovalFlow.value, routeName: 'CompaniesPostsApprovalFlowForm' },
  { key: 'reviewReplyApprovalFlow', featureFlag: providedFeaturesReviewReplyApprovalFlow.value, routeName: 'CompaniesReviewReplyApprovalFlowForm' },
  { key: 'mapionBizSearchGroup', featureFlag: showSearchGroups.value, routeName: 'CompaniesMapionBizSearchGroups' },
  { key: 'mapionBizFreeItems', featureFlag: showTemplates.value, routeName: 'CompaniesMapionBizBannerTemplates' },
  { key: 'searchKeywordImpressionLabel', featureFlag: providedFeaturesAnalysisSearchKeywordImpression.value, routeName: 'CompaniesSearchKeywordImpressionLabels' },
]

const getFunctionSettingValue = (key: FunctionSettingKey): string => {
  const hasSearchKeywords = searchKeywordImpressionLabels.value.some(label => label.keywords.length > 0)

  switch (key) {
  case 'snsApprovalFlow':
    return company.value.attributes.sns_approval_flow ? t('pages.companies.management.functionSettings.snsApprovalFlow.on') : t('pages.companies.management.functionSettings.snsApprovalFlow.off')
  case 'postApprovalFlow':
    return companyApprovalFlow.value?.postApprovalFlow ? t('pages.companies.management.functionSettings.postApprovalFlow.on') : t('pages.companies.management.functionSettings.postApprovalFlow.off')
  case 'reviewReplyApprovalFlow':
    return company.value.attributes.reply_approval_flow ? t('pages.companies.management.functionSettings.reviewReplyApprovalFlow.on') : t('pages.companies.management.functionSettings.reviewReplyApprovalFlow.off')
  case 'mapionBizSearchGroup':
    return (companiesMapionBizSearchGroups.value?.length || 0).toString()
  case 'mapionBizFreeItems':
    return (companyMapionBizBannersTemplate.value?.length || 0).toString()
  case 'searchKeywordImpressionLabel':
    return hasSearchKeywords ? t('pages.companies.management.functionSettings.searchKeywordImpressionLabel.on') : t('pages.companies.management.functionSettings.searchKeywordImpressionLabel.off')
  default:
    return ''
  }
}

const functionSettings = computed<FunctionSetting[]>(() => {
  return functionSettingsKeys
    .filter(({ featureFlag }) => featureFlag)
    .map(({ key, routeName }) => {
      const setting = tm(`pages.companies.management.functionSettings.${key}`) as Setting

      if (typeof setting === 'object') {
        let countUnit = undefined
        if (key === 'mapionBizSearchGroup' || key === 'mapionBizFreeItems') {
          countUnit = t('pages.companies.management.countUnit')
        }

        if (key === 'mapionBizSearchGroup') {
          return {
            ...setting,
            title: t('pages.companies.mapionBizSearchGroupsForm.title'),
            routeObj: { name: 'CompaniesMapionBizSearchGroups', params: { company_uuid: currentCompanyUuid.value } },
            name: key,
            value: getFunctionSettingValue(key),
            countUnit: countUnit,
          }
        }

        return {
          ...setting,
          title: t(`pages.companies.management.functionSettings.${key}.title`),
          routeObj: { name: routeName, params: { company_uuid: currentCompanyUuid.value } },
          name: key,
          value: getFunctionSettingValue(key),
          countUnit: countUnit,
          on: t(`pages.companies.management.functionSettings.${key}.on`, ''),
          off: t(`pages.companies.management.functionSettings.${key}.off`, ''),
        } as FunctionSetting
      }
      return undefined
    })
    .filter((setting): setting is FunctionSetting => setting !== undefined)
})

// Vuex Actions
const readTagGroups = () => { store.dispatch('companiesTagGroups/readTagGroups') }

const deleteOriginalTagGroup = async (originalTagGroupKind: string) => { return await store.dispatch('companiesOriginalTagGroup/deleteOriginalTagGroup', originalTagGroupKind) }

const readCompany = async (uuid: string) => { await store.dispatch('companiesCompany/readCompany', uuid) }

const readApprovalFlow = () => { store.dispatch('companiesPostsManagementsApprovalFlows/readApprovalFlow') }

const readMapionBizBannersTemplates = () => { store.dispatch('companiesMapionBizBannersTemplates/readMapionBizBannersTemplates') }

const readMapionBizSearchGroups = () => { store.dispatch('companiesMapionBizSearchGroups/readMapionBizSearchGroups') }

const handleClick = () => {
  router.push({ name: 'CompaniesManagementOriginalTagGroupsNew', params: { company_uuid: currentCompanyUuid.value } })
}

const handleDelete = async (originalTagGroupKind: string) => {
  if (confirm('分析軸（駐車場）を削除してよろしいですか？\n' +
    '削除した場合、店舗のオプション設定内容や分析画面からも削除されます。')) {
    const isSuccess = await deleteOriginalTagGroup(originalTagGroupKind)
    if (isSuccess) readTagGroups()
  }
}

// original_tag_group_aからaだけを取り出す
const extractOriginalTagGroupKind = (kind: string): OriginalTagGroupKindAlphabet => {
  const kindArr = kind.split('_')
  return kindArr[kindArr.length - 1] as OriginalTagGroupKindAlphabet
}

const makeOriginalTagGroupSetting = (kind: string): OriginalTagGroupSetting | undefined => {
  const originalTagGroupKind = extractOriginalTagGroupKind(tagGroups.value[kind].kind)
  const originalTagGroups = tm('pages.companies.management.customSettings.originalTagGroups') as Setting
  if (typeof originalTagGroups === 'object') {
    return {
      ...originalTagGroups,
      registeredCount: tagGroups.value[kind].registeredCount,
      title: tagGroups.value[kind].label,
      routeObj: { name: 'CompaniesManagementOriginalTagGroupsEdit', params: { company_uuid: currentCompanyUuid.value, tag_group_kind: originalTagGroupKind } },
      originalTagGroupKind: originalTagGroupKind,
    }
  }
}

const load = async () => {
  readTagGroups()
  if (showOnlyOwnerAuthority.value) {
    readApprovalFlow()
    await readCompany(currentCompanyUuid.value)

    if (providedFeaturesAnalysisSearchKeywordImpression.value) { readLabels() }
    if (showSearchGroups.value) {
      readMapionBizSearchGroups()
      readMapionBizBannersTemplates()
    }
  }
}

load()
</script>

<template>
  <div>
    <div v-if="!loading && basicSettings.length !== 0" class="p-box">
      <div class="p-box__header">
        <p class="p-box__title">
          {{ t('pages.companies.management.basicSettings.title') }}
        </p>
      </div>
      <div class="p-box__body">
        <div class="settingBox c-grid__items">
          <div v-for="(setting, index) in basicSettings" :key="`basicSettings_${index}`"
               class="c-grid__item__s__12 c-grid__item__m__4 c-grid__item__l__4"
          >
            <div class="p-box">
              <div class="p-box__header">
                <p class="p-box__title">
                  {{ setting.title }}
                </p>
              </div>
              <div class="p-box__body">
                <p class="settingBox__description">
                  {{ setting.description }}
                </p>
                <div class="settingBox__bottom">
                  <p>
                    {{ setting.registeredItemName }}: <span class="settingBox__count">{{ setting.registeredCount
                    }}</span>{{ t('pages.companies.management.countUnit') }}
                  </p>
                  <router-link v-if="showOnlyOwnerAuthority || setting.name !== 'keywords'" :to="setting.routeObj"
                               class="c-button__item c-button__item--primary settingBox__btn"
                  >
                    {{ setting.btnText }}
                  </router-link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-if="!loading" class="p-box">
      <div class="p-box__header">
        <p class="p-box__title">
          {{ t('pages.companies.management.customSettings.title') }}
        </p>
      </div>
      <div class="p-box__body">
        <div class="settingBox c-grid__items">
          <div v-for="(setting, index) in customSettings" :key="`customSettings_${index}`"
               class="c-grid__item__s__12 c-grid__item__m__4 c-grid__item__l__4"
          >
            <div class="p-box">
              <div class="p-box__header">
                <p class="p-box__title">
                  {{ setting.title }}
                </p>
              </div>
              <div class="p-box__body">
                <p class="settingBox__description">
                  {{ setting.description }}
                </p>
                <div class="settingBox__bottom">
                  <p>
                    {{ setting.registeredItemName }}: <span class="settingBox__count">{{ setting.registeredCount
                    }}</span>{{ t('pages.companies.management.countUnit') }}
                  </p>
                  <div class="c-button c-button--center">
                    <button
                      v-if="setting.originalTagGroupKind"
                      class="c-button__item c-button__item--outline-cancel settingBox__btn"
                      @click="handleDelete(setting.originalTagGroupKind ?? '')"
                    >
                      {{ t('labels.delete') }}
                    </button>
                    <router-link :to="setting.routeObj" class="c-button__item c-button__item--primary settingBox__btn">
                      {{ setting.btnText }}
                    </router-link>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div v-if="canAddTagGroup" class="c-grid__item__s__12 c-grid__item__m__4 c-grid__item__l__4">
            <div class="addOriginalTagGroupBox" @click="handleClick">
              <div class="addOriginalTagGroupBox__content">
                <div class="addOriginalTagGroupBox__iconArea">
                  <KkIcon class="addOriginalTagGroupBox__iconArea__icon" name="iconPlusRed" />
                </div>
                <p class="addOriginalTagGroupBox__btn">
                  {{ t('pages.companies.management.addOriginalTagGroup') }}
                </p>
                <div class="addOriginalTagGroupBox__description">
                  {{ t('pages.companies.management.originalTagGroupDescription1') }}
                  {{ t('pages.companies.management.originalTagGroupDescription2') }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div v-if="!functionSettingsLoading && showOnlyOwnerAuthority && functionSettings.length !== 0" class="p-box">
      <div class="p-box__header">
        <p class="p-box__title">
          {{ t('pages.companies.management.functionSettings.title') }}
        </p>
      </div>
      <div class="p-box__body">
        <div class="settingBox c-grid__items">
          <div v-for="(setting, index) in functionSettings" :key="`functionSettings_${index}`"
               class="c-grid__item__s__12 c-grid__item__m__4 c-grid__item__l__4"
          >
            <div class="p-box">
              <div class="p-box__header">
                <p class="p-box__title">
                  {{ setting.title }}
                </p>
              </div>
              <div class="p-box__body">
                <p class="settingBox__description">
                  {{ setting.description }}
                </p>
                <div class="settingBox__bottom">
                  <p>
                    {{ setting.registeredItemName }}: <span class="settingBox__count">{{ setting.value }}</span><span
                      v-if="setting.countUnit"
                    >{{ setting.countUnit }}</span>
                  </p>
                  <div class="c-button c-button--center">
                    <router-link :to="setting.routeObj" class="c-button__item c-button__item--primary settingBox__btn">
                      {{ setting.btnText }}
                    </router-link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <KkLoading v-if="functionSettingsLoading" />
  </div>
</template>

<style lang="scss" scoped>
@import '../../../stylesheets/biz_spa/foundation/_var.scss';

.settingBox {
  margin-bottom: 0 !important;

  .p-box {
    margin: 12px;
  }

  .p-box__title {
    font-size: 18px;
  }

  &__description {
    margin-bottom: 24px;
  }

  &__bottom {
    margin-top: 24px;
    text-align: center;
  }

  &__count {
    color: $color-accent-primary;
    font-size: 24px;
    font-weight: 700;
  }
}

.addOriginalTagGroupBox {
  display: flex;
  align-content: center;
  height: 318px;
  margin: 12px;
  background: $color-base-i;
  border: 1px solid $color-base-i;
  border-radius: 4px;

  &__content {
    margin: auto;
  }

  &__iconArea {
    display: flex;
    justify-content: center;
    &__icon {
      width: 32px;
      height: 32px;
      margin-bottom: 12px;
    }
  }

  &__btn {
    margin-top: 12px;
    text-align: center;
  }

  &__description {
    text-align: center;
    margin: 24px 32px 0;
  }

  &:hover {
    cursor: pointer;
  }
}
</style>
